import React, { useRef, useState } from 'react';
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { exchangeRequest, passkeyError } from './api';
import { Icon, Notice } from './components';
import PlatformHeader from './PlatformHeader';

const EXCHANGE_COPY = {
  section: undefined,
  storyEyebrow: 'ESPACE ADMINISTRATIF',
  storyTitle: 'Inscriptions des étudiants en échange',
  storyText: 'Retrouvez les dossiers des étudiants en échange et suivez leur inscription au sein de la plateforme ICES.',
  storyFooter: 'ICES · Relations internationales',
  storyIcon: 'globe',
  cardEyebrow: 'ÉTUDIANTS EN ÉCHANGE'
};

// `request` et `copy` permettent au portail d'administration de réutiliser
// cette connexion (mêmes comptes, mêmes routes /auth sous un autre préfixe).
export default function Login({ onAuthenticated, initialError, request = exchangeRequest, copy = EXCHANGE_COPY }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [firstConnection, setFirstConnection] = useState(false);
  const [flow, setFlow] = useState(null);
  const [keyName, setKeyName] = useState('Ma clé d’accès');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const passwordToSave = useRef('');
  const supported = window.isSecureContext && browserSupportsWebAuthn();

  async function login(event) {
    event.preventDefault(); setError(''); setBusy(true);
    try {
      const submittedPassword = password;
      const next = await request('/auth/login', { method: 'POST', body: { username, password, activationCode: firstConnection ? activationCode.trim() : '' } });
      passwordToSave.current = submittedPassword;
      setFlow(next); setActivationCode('');
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function verify() {
    setError(''); setBusy(true);
    let submitted = false;
    try {
      const credential = flow.kind === 'authentication'
        ? await startAuthentication({ optionsJSON: flow.options })
        : await startRegistration({ optionsJSON: flow.options });
      submitted = true;
      await request('/auth/verify', { method: 'POST', csrf: flow.csrf, body: { credential, name: keyName } });
      // Les formulaires React n’entraînent pas toujours la proposition native de Chrome.
      // Cette API laisse le navigateur demander explicitement l’accord de l’utilisateur.
      if (passwordToSave.current && window.PasswordCredential && navigator.credentials?.store) {
        try {
          const passwordCredential = new window.PasswordCredential({
            id: username,
            password: passwordToSave.current,
            name: username
          });
          await navigator.credentials.store(passwordCredential);
        } catch {
          // L’enregistrement reste facultatif et dépend des réglages du navigateur.
        }
      }
      passwordToSave.current = '';
      setPassword('');
      await onAuthenticated();
    } catch (e) {
      setError(passkeyError(e));
      if (submitted) setFlow(null);
    } finally { setBusy(false); }
  }

  return <><PlatformHeader section={copy.section} /><div className="ex-login-layout">
    <aside className="ex-login-story">
      <div className="ex-login-illustration" aria-hidden="true"><Icon name={copy.storyIcon} size={150} /><span className="ex-orbit ex-orbit--one" /><span className="ex-orbit ex-orbit--two" /></div>
      <p className="ex-eyebrow">{copy.storyEyebrow}</p><h1>{copy.storyTitle}</h1><p>{copy.storyText}</p>
      <span className="ex-login-footer">{copy.storyFooter}</span>
    </aside>
    <main className="ex-login-main"><div className="ex-login-card"><div className="ex-feature-icon"><Icon name="key" size={27} /></div>
      <p className="ex-eyebrow">{copy.cardEyebrow}</p><h2>{flow ? (flow.kind === 'authentication' ? 'Votre clé d’accès' : 'Enregistrez votre clé') : 'Connexion à votre espace'}</h2>
      <p className="ex-muted">{flow ? 'Validez avec votre appareil ou votre gestionnaire de clés d’accès.' : 'Utilisez votre identifiant et votre mot de passe, puis confirmez avec votre clé d’accès.'}</p>
      <ol className="ex-login-steps" aria-label="Étapes de connexion"><li className={!flow ? 'is-active' : 'is-complete'}><span>{flow ? '✓' : '1'}</span>Identifiants</li><li className={flow ? 'is-active' : ''}><span>2</span>Clé d’accès</li></ol>
      <Notice>{error || initialError}</Notice>
      {!supported && <Notice>Ce navigateur doit prendre en charge les clés d’accès et ouvrir cette interface en HTTPS (ou sur localhost).</Notice>}
      {!flow ? <form onSubmit={login} className="ex-form">
        <label>Identifiant<input autoComplete="username" value={username} onChange={event => setUsername(event.target.value)} required maxLength={190} autoFocus /></label>
        <label>Mot de passe<input type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required maxLength={256} /></label>
        <label className="ex-checkbox"><input type="checkbox" checked={firstConnection} onChange={event => setFirstConnection(event.target.checked)} />Première connexion ou réactivation</label>
        {firstConnection && <label>Code d’activation<input autoComplete="off" value={activationCode} onChange={event => setActivationCode(event.target.value)} required maxLength={64} /><small>Le code à usage unique remis par votre administrateur.</small></label>}
        <button className="ex-button ex-button--primary ex-button--full" disabled={busy || !supported}>{busy ? 'Vérification…' : 'Continuer'}<Icon name="arrow" /></button>
      </form> : <div className="ex-form">
        <p>Compte : <strong>{username}</strong></p>
        {flow.kind !== 'authentication' && <label>Nom de la clé<input value={keyName} onChange={event => setKeyName(event.target.value)} maxLength={100} placeholder="Mon Mac ou Google Password Manager" /></label>}
        <button className="ex-button ex-button--primary ex-button--full" onClick={verify} disabled={busy}><Icon name="key" />{busy ? 'Validation en cours…' : flow.kind === 'authentication' ? 'Utiliser ma clé d’accès' : 'Créer ma clé d’accès'}</button>
        <button className="ex-button ex-button--full" onClick={() => { setFlow(null); setError(''); }} disabled={busy}>Revenir aux identifiants</button>
      </div>}
      <p className="ex-login-help">Les clés d’accès peuvent être conservées sur votre Mac ou dans Google Password Manager. En cas de perte d’accès, contactez votre administrateur.</p>
    </div></main>
  </div></>;
}
